import React from 'react';

interface Props {
  onBack: () => void;
}

export const TalkToTaskSupport: React.FC<Props> = ({ onBack }) => {
  return (
    <div className="bg-slate-50 min-h-full font-sans flex flex-col">
      <nav className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-10 flex-none">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
           <h1 className="font-bold text-xl text-slate-900 tracking-tight">TalkToTask</h1>
           <button onClick={onBack} className="text-sm font-medium text-slate-600 hover:text-slate-900">
             Home
           </button>
        </div>
      </nav>

      <main className="flex-1 w-full max-w-4xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12 animate-fade-in-up">
            <h1 className="text-4xl font-extrabold text-slate-900 mb-4">How can we help you?</h1>
            <p className="text-lg text-slate-600">We're here to ensure you have the best experience with TalkToTask.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
            
            {/* Contact Section */}
            <div className="md:col-span-1 space-y-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 h-full flex flex-col">
                    <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center mb-4 text-2xl">
                        ✉️
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">Contact Us Directly</h2>
                    <p className="text-slate-600 text-sm mb-6 flex-1">
                        Need assistance? Found a bug? We reply to every email.
                    </p>
                    
                    <a 
                        href="mailto:mactrixapp@gmail.com" 
                        className="block w-full py-3 bg-slate-900 text-white text-center font-bold rounded-xl hover:bg-slate-800 transition-colors shadow-lg"
                    >
                        Email Support
                    </a>
                    <p className="text-xs text-slate-400 mt-3 text-center">
                        Please include your device model for faster assistance.
                    </p>
                </div>
            </div>

            {/* FAQs Section */}
            <div className="md:col-span-2">
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Common Questions</h2>
                    
                    <div className="space-y-6">
                        <div className="pb-6 border-b border-slate-50 last:border-0 last:pb-0">
                            <h3 className="font-bold text-slate-800 mb-2 flex items-start gap-2">
                                <span className="text-indigo-500">Q:</span> How do I restore my purchase?
                            </h3>
                            <p className="text-slate-600 text-sm leading-relaxed pl-6">
                                Go to the app <strong>Settings</strong> screen (usually the gear icon) and tap the <strong>"Restore Purchase"</strong> button. This will validate your subscription with the App Store/Play Store and unlock your Pro features instantly.
                            </p>
                        </div>

                        <div className="pb-6 border-b border-slate-50 last:border-0 last:pb-0">
                            <h3 className="font-bold text-slate-800 mb-2 flex items-start gap-2">
                                <span className="text-indigo-500">Q:</span> Is my data private?
                            </h3>
                            <p className="text-slate-600 text-sm leading-relaxed pl-6">
                                <strong>Yes!</strong> All your notes, transcripts, and tasks are stored locally on your device using a local database. We do not have access to your personal content. Audio is processed ephemerally by our AI partner and is not used for training.
                            </p>
                        </div>

                        <div className="pb-6 border-b border-slate-50 last:border-0 last:pb-0">
                            <h3 className="font-bold text-slate-800 mb-2 flex items-start gap-2">
                                <span className="text-indigo-500">Q:</span> Can I share my tasks?
                            </h3>
                            <p className="text-slate-600 text-sm leading-relaxed pl-6">
                                Absolutely. Use the <strong>Share</strong> button inside any session detail view to export your summary, tasks, or full transcript to other apps (like Notes, Email, or Messaging apps) or people.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 bg-indigo-50 border border-indigo-100 p-6 rounded-2xl flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h3 className="font-bold text-indigo-900">Have a Feature Request?</h3>
                        <p className="text-sm text-indigo-700">We value user input to improve the app.</p>
                    </div>
                    <a href="mailto:mactrixapp@gmail.com?subject=Feature Request for TalkToTask" className="text-indigo-600 font-bold text-sm hover:underline">
                        Send Feedback →
                    </a>
                </div>
            </div>
        </div>
      </main>

      <div className="mt-12 py-8 text-center border-t border-slate-200">
            <a href="/" onClick={(e) => { e.preventDefault(); onBack(); }} className="text-slate-500 hover:text-indigo-600 font-medium transition-colors">
                ← Back to Home
            </a>
      </div>
    </div>
  );
};